import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Users,
  BookOpen,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import { useEffect, useState } from "react";
import { getDashboardDetails } from "../../services/dashboardService";
import { DashboardDetails } from "../../types/dashboard";
import { Spinner } from "../ui/Spinner";
import { useUser } from '../../contexts/UserContext';

export function AdminDashboard() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardDetails | null>(null);
  const { currentUser } = useUser();

  useEffect(() => {
    const fetchDetails = async () => {
      if (!currentUser) return;
      const identifier = currentUser.role === 'corporate_admin'
        ? currentUser.username
        : currentUser.role === 'distributor_admin'
          ? currentUser.distributorCode
          : currentUser.username;
      const data = await getDashboardDetails(identifier, currentUser.role);
      setDashboardData(data);
    };
    if (currentUser) {
    fetchDetails();
    }
  }, [currentUser]);

  const stats = [
    {
      title: "Total Sellers",
      value: dashboardData?.totalUsers,
      icon: Users,
      color: "text-blue-500",
      bgColor: "bg-blue-100",
    },
    {
      title: "Active Modules",
      value: dashboardData?.activeModules,
      icon: BookOpen,
      color: "text-green-500",
      bgColor: "bg-green-100",
    },
    {
      title: "Total Trainers",
      value: dashboardData?.totalTrainers,
      icon: GraduationCap,
      color: "text-purple-500",
      bgColor: "bg-purple-100",
    },
  ];

  const quickActions = [
    {
      title: "Manage Users",
      description: "Import and map user accounts",
      icon: Users,
      path: "/users",
    },
    {
      title: "Module Configuration",
      description: "Configure learning modules and content",
      icon: BookOpen,
      path: "/modules",
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        {currentUser?.role === 'corporate_admin' && (
        <Button onClick={() => navigate("/modules/new")}>
          Create New Module
        </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <div className={`p-2 rounded-full ${stat.bgColor}`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold min-h-[2.5rem] flex items-center justify-left">
                {stat.value === undefined || stat.value === null ? <Spinner /> : stat.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {quickActions.map((action) => (
          <Card
            key={action.title}
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(action.path)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{action.title}</CardTitle>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {action.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
} 